/**
 * BusinessEnvironmentFactorForm Component
 *
 * Reusable form for creating and editing business environment factors
 */

import { TextField, type CreateBusinessEnvironmentFactorFormData } from '../forms';
import { createBusinessEnvironmentFactorSchema } from '../forms/validation';
import { EntityForm } from '../shared';
import { TagPicker } from '../tags/TagPicker';
import '@tylertech/forge/card';
import '@tylertech/forge/icon';
import './BusinessEnvironmentFactorForm.css';

export interface BusinessEnvironmentFactorFormProps {
  /** Initial form values (for edit mode) */
  defaultValues?: Partial<CreateBusinessEnvironmentFactorFormData>;
  /** Submit handler */
  onSubmit: (data: CreateBusinessEnvironmentFactorFormData) => void | Promise<void>;
  /** Cancel handler */
  onCancel: () => void;
  /** Is form submitting */
  isSubmitting?: boolean;
  /** Form mode */
  mode: 'create' | 'edit';
}

const CardElement = 'forge-card' as React.ElementType;

/**
 * BusinessEnvironmentFactor create/edit form with validation
 *
 * @example
 * ```tsx
 * <BusinessEnvironmentFactorForm
 *   mode="create"
 *   onSubmit={handleCreate}
 *   onCancel={() => navigate('/business-environment-factors')}
 * />
 * ```
 */
export function BusinessEnvironmentFactorForm({
  defaultValues,
  onSubmit,
  onCancel,
  isSubmitting = false,
  mode,
}: BusinessEnvironmentFactorFormProps): JSX.Element {
  return (
    <EntityForm<CreateBusinessEnvironmentFactorFormData>
      schema={createBusinessEnvironmentFactorSchema}
      baseValues={{
        tags: [],
      }}
      defaultValues={defaultValues}
      onSubmit={onSubmit}
      onCancel={onCancel}
      isSubmitting={isSubmitting}
      mode={mode}
      createLabel="Create Business Environment Factor"
      formClassName="business-environment-factor-form"
    >
      {({ register, formState: { errors }, values, setValue }) => {
        const tags = values.tags || [];

        return (
          <>
            <CardElement className="form-card">
              <div className="form-section">
                <h2 className="forge-typography--headline6">Basic Information</h2>

                <TextField
                  label="Name"
                  {...register('name')}
                  value={values.name || ''}
                  error={errors.name}
                  required
                  placeholder="e.g., Regulatory Mandate"
                  helperText="A descriptive name for the business environment factor"
                />

                <TextField
                  label="Description"
                  {...register('description')}
                  value={values.description || ''}
                  error={errors.description}
                  placeholder="Brief description of the business environment factor"
                  helperText="Optional description (max 2000 characters)"
                />
              </div>
            </CardElement>

            <CardElement className="form-card">
              <div className="form-section">
                <h2 className="forge-typography--headline6">Classification</h2>

                <TextField
                  label="Category"
                  {...register('category')}
                  value={values.category || ''}
                  error={errors.category}
                  placeholder="e.g., Regulatory"
                  helperText="Optional category"
                />

                <TextField
                  label="Label"
                  {...register('label')}
                  value={values.label || ''}
                  error={errors.label}
                  placeholder="e.g., High Impact"
                  helperText="Optional label"
                />
              </div>
            </CardElement>

            <CardElement className="form-card">
              <div className="form-section">
                <h2 className="forge-typography--headline6">Additional Information</h2>

                <TagPicker
                  id="business-environment-factor-tag-picker"
                  label="Tags"
                  selectedTagIds={tags}
                  onChange={(tagIds) => setValue('tags', tagIds)}
                  disabled={isSubmitting}
                />

                <TextField
                  label="Notes"
                  {...register('notes')}
                  value={values.notes || ''}
                  error={errors.notes}
                  placeholder="Additional notes about this business environment factor"
                  helperText="Optional notes (max 5000 characters)"
                />
              </div>
            </CardElement>
          </>
        );
      }}
    </EntityForm>
  );
}
